import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import userService, { CreateUserDto, UpdateUserDto, UserQueryParams } from '../services/user.service';

export const useUsersPage = (params: UserQueryParams) => {
  return useQuery({
    queryKey: ['users', 'page', params],
    queryFn: () => userService.getUsers(params),
    placeholderData: keepPreviousData,
  });
};

export const useUserSummary = () => {
  return useQuery({
    queryKey: ['users', 'summary'],
    queryFn: () => userService.getSummary(),
  });
};

export const useRoles = () => {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => userService.getRoles(),
    staleTime: 60_000,
  });
};

// The latest things a user did or that were done to the account
export const useUserActivities = (userId: string | null, limit = 10) => {
  return useQuery({
    queryKey: ['users', 'activities', userId, limit],
    queryFn: () => userService.getUserActivities(userId as string, limit),
    enabled: !!userId,
  });
};

const useInvalidateUsers = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['users'] });
};

export const useCreateUser = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (data: CreateUserDto) => userService.createUser(data),
    onSuccess: invalidate,
  });
};

export const useUpdateUser = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) => userService.updateUser(id, data),
    onSuccess: invalidate,
  });
};

export const useDeleteUser = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: invalidate,
  });
};

export const useRestoreUser = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (id: string) => userService.restoreUser(id),
    onSuccess: invalidate,
  });
};

export const useResetTwoFactor = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (id: string) => userService.resetTwoFactor(id),
    onSuccess: invalidate,
  });
};

export const useResetUserPassword = () => {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) => userService.resetUserPassword(id, newPassword),
    onSuccess: invalidate,
  });
};
